import React, { useCallback, useContext, useEffect, useState } from "react";
import { Panel, Title, Text, Pill, Table, Icon, ButtonLink, Button, Skeleton } from "components/commons";
import { Collapse, Divider } from "antd";
import lang from "translations";
import { PillType, StyleType } from "enums";
import { Path } from "paths";
import { VenueContext } from "contexts";
import { v4 as uuidv4 } from "uuid";
import PrintableBill from "./printable-bill";

const reconciliationPillType = (status) => {
  if (status === "RECONCILED") return PillType.Green;
  if (status === "UNRECONCILED") return PillType.Red;
  return PillType.Yellow;
};

const billStatusPillType = (status) => {
  switch (status) {
    case "SETTLED":
      return PillType.Green;
    case "VOID":
      return PillType.Red;
    default:
      return PillType.Yellow;
  }
};

const splitColumns = [
  { text: lang.splitNumber, key: "splitNumber" },
  { text: lang.guest, key: "guestRef" },
  { text: lang.splitType, key: "splitType" },
  { text: lang.amount, key: "splitAmount", align: "right" },
  { text: lang.paidAmount, key: "paidAmount", align: "right" },
  { text: lang.reconciliationStatus, key: "reconciliationStatus", custom: true },
];

const itemColumns = [
  { text: lang.productName, key: "productName" },
  { text: lang.quantity, key: "quantity", align: "right" },
  { text: lang.amount, key: "amount", align: "right" },
];

const paymentColumns = [
  { text: lang.method, key: "method" },
  { text: lang.amount, key: "amount", align: "right" },
  { text: lang.reference, key: "referenceId" },
  { text: lang.status, key: "status" },
  { text: lang.date, key: "paidAt" },
  { text: lang.selected, key: "isSelected", custom: true },
];

const BillCard = ({ bill, onPrint }) => {
  const splitRows = bill.splits.map((split) => ({
    ...split,
    guestRef: split.guestRef || "-",
    reconciliationStatus: (
      <Pill size="text-xs" type={reconciliationPillType(split.reconciliationStatus)}>
        {split.reconciliationStatus}
      </Pill>
    ),
  }));

  return (
    <Panel bordered mb="0">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-md">
        <div className="flex items-center gap-2">
          <Icon name="bill" className="text-pelorous" fontSize="18px" />
          <Text strong>{lang.bill} #{bill.billHeaderId}</Text>
        </div>
        <div className="flex items-center gap-2">
          <Pill type={billStatusPillType(bill.status)}>{bill.status}</Pill>
          {bill.resolvedTransactionId && (
            <ButtonLink newTabPath={Path.TRANSACTION_ID(bill.resolvedTransactionId)}>
              <Pill size="text-xs" type={PillType.Blueish}>
                <Icon name="receipt" fontSize="10px" className="mr-xs" />
                {lang.viewTransaction} #{bill.resolvedTransactionId}
              </Pill>
            </ButtonLink>
          )}
          <Button type={StyleType.Secondary} onClick={() => onPrint(bill)}>
            {lang.print}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Text uppercase strong color="text-gray" size="text-xs">{lang.openedAt}</Text>
          <Text className="mt-xs">{bill.openedAt}</Text>
        </div>
        <div>
          <Text uppercase strong color="text-gray" size="text-xs">{lang.settledAt}</Text>
          <Text className="mt-xs">{bill.settledAt}</Text>
        </div>
        <div className="text-right col-span-2">
          <Text uppercase strong color="text-gray" size="text-xs">{lang.paidAmount}</Text>
          <Title lg className="mt-xs">{bill.paidAmount}</Title>
        </div>
      </div>
      <Divider className="my-md" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-md">
        <div>
          <Text uppercase strong color="text-gray" size="text-xs">{lang.grossAmount}</Text>
          <Text className="mt-xs">{bill.grossAmount}</Text>
        </div>
        <div>
          <Text uppercase strong color="text-gray" size="text-xs">{lang.discountAmount}</Text>
          <Text className="mt-xs">{bill.discountAmount}</Text>
        </div>
        <div>
          <Text uppercase strong color="text-gray" size="text-xs">{lang.taxAmount}</Text>
          <Text className="mt-xs">{bill.taxAmount}</Text>
        </div>
        <div>
          <Text uppercase strong color="text-gray" size="text-xs">{lang.netAmount}</Text>
          <Text className="mt-xs">{bill.netAmount}</Text>
        </div>
      </div>

      {bill.splits.length > 0 && (
        <>
          <div className="overflow-x-auto overflow-hidden">
            <Table data={splitRows} columns={splitColumns} minWidth="700px" />
          </div>
          <Collapse ghost className="mt-sm">
            {bill.splits.map((split) => (
              <Collapse.Panel
                key={split.billSplitId}
                header={
                  <Text size="text-sm">
                    {lang.splitNumber} {split.splitNumber} — {lang.productName} ({split.items.length}), {lang.paymentAttempts} ({split.payments.length})
                  </Text>
                }
              >
                {split.items.length > 0 && (
                  <div className="overflow-x-auto overflow-hidden mb-sm">
                    <Table data={split.items} columns={itemColumns} minWidth="500px" />
                  </div>
                )}
                <div className="overflow-x-auto overflow-hidden">
                  <Table
                    data={split.payments.map((payment) => ({
                      ...payment,
                      isSelected: payment.isSelected ? (
                        <Pill size="text-xs" type={PillType.Green}>
                          <Icon name="check-only" fontSize="9px" className="mr-xs" />
                          {lang.selected}
                        </Pill>
                      ) : (
                        <Text color="text-gray-400">-</Text>
                      ),
                    }))}
                    columns={paymentColumns}
                    minWidth="700px"
                  />
                </div>
              </Collapse.Panel>
            ))}
          </Collapse>
        </>
      )}
    </Panel>
  );
};

const ReservationBilling = ({ data, loading }) => {
  const { venue } = useContext(VenueContext);
  const bills = data?.bills || [];
  const [printingBill, setPrintingBill] = useState(null);

  const handlePrint = useCallback((bill) => {
    setPrintingBill(bill);
  }, []);

  useEffect(() => {
    if (!printingBill) {
      return;
    }
    window.print();
    const reset = () => setPrintingBill(null);
    window.addEventListener("afterprint", reset);
    return () => window.removeEventListener("afterprint", reset);
  }, [printingBill]);

  if (loading) {
    return (
      <Panel className="mt-md">
        <Skeleton />
      </Panel>
    );
  }

  if (!bills.length) {
    return (
      <Panel className="mt-md">
        <Text color="text-gray-400">{lang.noBillsYet}</Text>
      </Panel>
    );
  }

  return (
    <>
      <div className="mt-md grid grid-cols-1 gap-3">
        {bills.map((bill) => (
          <BillCard key={bill.billHeaderId || uuidv4()} bill={bill} onPrint={handlePrint} />
        ))}
      </div>
      <PrintableBill bill={printingBill} venueName={venue?.venueName} />
    </>
  );
};

export default ReservationBilling;
